import { useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Loader2, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { API, resolveImageUrl } from "@/lib/apiBase";

const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export function ImageUploadField({ value, onChange, testPrefix = "img" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    if (!ALLOWED.includes(file.type)) return setError("Only JPG, PNG or WEBP images are allowed");
    if (file.size > MAX_BYTES) return setError("Image must be 5 MB or smaller");
    const body = new FormData();
    body.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/images`, body);
      onChange(res.data.image_url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="md:col-span-2" data-testid={`${testPrefix}-field`}>
      <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">Bottle Photo</Label>
      <div className="flex items-center gap-4">
        <div className="w-20 h-28 rounded-sm border border-white/10 bg-white/[0.03] flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <img src={resolveImageUrl(value)} alt="Bottle preview" className="w-full h-full object-contain" data-testid={`${testPrefix}-preview`} />
          ) : (
            <ImagePlus className="w-6 h-6 text-white/20" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept={ALLOWED.join(",")} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid={`${testPrefix}-input`} />
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testPrefix}-upload-btn`}
            className="inline-flex items-center gap-2 px-3 h-9 rounded-sm border border-white/10 text-white/80 font-body text-sm hover:border-gold/50 hover:text-gold transition-colors duration-200 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            {uploading ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
          </button>
          {value && !uploading && (
            <button type="button" onClick={() => onChange(null)} data-testid={`${testPrefix}-remove-btn`} className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-merlot-light font-body transition-colors duration-200">
              <X className="w-3 h-3" /> Remove
            </button>
          )}
          <p className="font-body text-[11px] text-white/30">JPG, PNG or WEBP · max 5 MB</p>
        </div>
      </div>
      {error && <p className="text-xs text-red-400 font-body mt-2" data-testid={`${testPrefix}-error`}>{error}</p>}
    </div>
  );
}
